import React, { FC, Fragment, useState } from 'react';
import type {
  DomainCount,
  WebWorkKindStats,
  WebWorkWeeklyBucket,
} from './types';
import { KIND_COLOURS } from './types';
import { DASH, deltaPct, fmtDelta, fmtInt, fmtPct, fmtShare } from './format';

/**
 * "Task kinds — most to least frequent". One row per kind (server already
 * ranks them); clicking a row expands the sites and hand-off reasons behind
 * it, and each reason lists the literal short reasons the classifier wrote.
 */

export interface KindTableProps {
  kinds: WebWorkKindStats[];
  weekly: WebWorkWeeklyBucket[];
  /** Weekday span label for the WoW footnote, e.g. "Mon→Thu". */
  spanLabel: string;
}

export const SiteList: FC<{ domains: DomainCount[]; total: number }> = ({
  domains,
  total,
}) => (
  <ul className='wwm-sites'>
    {domains.length === 0 && <li className='wwm-muted'>No sites recorded</li>}
    {domains.map((d) => (
      <li key={`${d.domain}-${d.inferred ? 'inf' : 'own'}`}>
        <span className={`wwm-site ${d.domain === 'unknown' ? 'unknown' : ''}`}>
          {d.domain}
        </span>
        {d.inferred && (
          <span
            className='wwm-badge inferred'
            title='Domain inferred from a sibling web action on the same sub-thread'
          >
            inferred
          </span>
        )}
        <span className='wwm-count'>{fmtInt(d.count)}</span>
        <span className='wwm-share'>{fmtShare(d.count, total)}</span>
      </li>
    ))}
  </ul>
);

const Sparkline: FC<{ values: number[]; colour: string }> = ({
  values,
  colour,
}) => {
  const max = Math.max(1, ...values.filter(Number.isFinite));
  return (
    <span className='wwm-spark' aria-hidden='true'>
      {values.map((v, i) => (
        <i
          key={i}
          style={{
            height: `${Math.max(2, (Math.max(0, v) / max) * 100)}%`,
            background: colour,
          }}
        />
      ))}
    </span>
  );
};

export const KindTable: FC<KindTableProps> = ({ kinds, weekly, spanLabel }) => {
  const [open, setOpen] = useState<string | null>(null);
  const [openReason, setOpenReason] = useState<string | null>(null);
  const grand = kinds.reduce((s, k) => s + k.total, 0);
  const current = weekly.find((w) => w.isCurrent) ?? null;
  const lastFull = [...weekly].reverse().find((w) => !w.isCurrent) ?? null;

  if (kinds.length === 0) {
    return (
      <p className='wwm-empty'>
        No classified human web work in this window yet.
      </p>
    );
  }

  return (
    <div className='wwm-table-wrap'>
      <table className='wwm-table' data-testid='kind-table'>
        <thead>
          <tr>
            <th>Kind</th>
            <th className='num'>Count</th>
            <th className='num'>Share</th>
            <th className='num'>This wk so far</th>
            <th className='num'>Last full wk</th>
            <th className='num'>WoW Δ*</th>
            <th>Trend</th>
          </tr>
        </thead>
        <tbody>
          {kinds.map((k) => {
            const isOpen = open === k.kind;
            const thisWeek = current?.byKind[k.kind] ?? 0;
            const lastFullWeek = lastFull?.byKind[k.kind] ?? 0;
            // Same-span comparison for the kind is not in the payload, so the
            // row shows this-week-so-far vs the last full week.
            const delta = fmtDelta(deltaPct(thisWeek, lastFullWeek));
            return (
              <Fragment key={k.kind}>
                <tr
                  className={`wwm-row ${isOpen ? 'open' : ''}`}
                  onClick={() => {
                    setOpen(isOpen ? null : k.kind);
                    setOpenReason(null);
                  }}
                  aria-expanded={isOpen}
                  data-testid={`kind-row-${k.kind}`}
                >
                  <td>
                    <span
                      className='wwm-swatch'
                      style={{ background: KIND_COLOURS[k.kind] }}
                    />
                    {k.label}
                  </td>
                  <td className='num'>{fmtInt(k.total)}</td>
                  <td className='num'>{fmtPct(k.sharePct)}</td>
                  <td className='num'>{fmtInt(thisWeek)}</td>
                  <td className='num'>{fmtInt(lastFullWeek)}</td>
                  <td className={`num wwm-delta ${delta.direction}`}>
                    {delta.label}
                  </td>
                  <td>
                    <Sparkline
                      values={weekly.map((w) => w.byKind[k.kind] ?? 0)}
                      colour={KIND_COLOURS[k.kind]}
                    />
                  </td>
                </tr>
                {isOpen && (
                  <tr
                    className='wwm-detail'
                    data-testid={`kind-detail-${k.kind}`}
                  >
                    <td colSpan={7}>
                      <div className='wwm-detail-cols'>
                        <div>
                          <h5>
                            Sites{' '}
                            <span className='wwm-muted'>
                              {fmtInt(k.webActions)} web_actions ·{' '}
                              {fmtInt(k.interventions)} interventions
                            </span>
                          </h5>
                          <SiteList domains={k.domains} total={k.total} />
                        </div>
                        <div>
                          <h5>Hand-off reasons</h5>
                          <ul className='wwm-reasons'>
                            {k.reasons.length === 0 && (
                              <li className='wwm-muted'>No reasons recorded</li>
                            )}
                            {k.reasons.map((r) => {
                              const rid = `${k.kind}:${r.reason}`;
                              const isReasonOpen = openReason === rid;
                              return (
                                <li key={r.reason}>
                                  <button
                                    type='button'
                                    className='wwm-linklike'
                                    onClick={() =>
                                      setOpenReason(isReasonOpen ? null : rid)
                                    }
                                    aria-expanded={isReasonOpen}
                                  >
                                    {r.label}
                                  </button>
                                  <span className='wwm-count'>
                                    {fmtInt(r.count)}
                                  </span>
                                  <span className='wwm-share'>
                                    {fmtPct(r.sharePct)}
                                  </span>
                                  {isReasonOpen && (
                                    <ul className='wwm-short-reasons'>
                                      {r.sampleShortReasons.length === 0 ? (
                                        <li className='wwm-muted'>{DASH}</li>
                                      ) : (
                                        r.sampleShortReasons.map((s) => (
                                          <li key={s}>“{s}”</li>
                                        ))
                                      )}
                                    </ul>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td>Total</td>
            <td className='num'>{fmtInt(grand)}</td>
            <td className='num'>{grand > 0 ? '100%' : DASH}</td>
            <td className='num'>{fmtInt(current?.total ?? 0)}</td>
            <td className='num'>{fmtInt(lastFull?.total ?? 0)}</td>
            <td className='num' />
            <td />
          </tr>
        </tfoot>
      </table>
      <p className='wwm-note'>
        * Row WoW Δ compares this week so far with the last full week; the
        headline tile compares {spanLabel} of this week with {spanLabel} of last
        week. Click a row for sites and hand-off reasons; click a reason for the
        literal short reasons.
      </p>
    </div>
  );
};
